// Iniciar atendimento no padrão Aeros Dark (mockup "Modal Novo Atendimento
// A"): card do contato, Fila e Conexão lado a lado, chips "Atribuir a"
// (admin escolhe o atendente; os demais abrem para si) e rodapé com
// "Abrir atendimento". A lógica de criação (POST /tickets, 403/409 de ticket
// já aberto, cadastro rápido de contato) é a de sempre.
import React, { useState, useEffect, useContext } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import ChatIcon from "@material-ui/icons/Chat";
import HistoryIcon from "@material-ui/icons/History";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import Switch from "@material-ui/core/Switch";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { substituteVariables } from "../../pages/QuickMessages/quickMessageRules";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ContactModal from "../ContactModal";
import ShowTicketOpen from "../ShowTicketOpenModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { avatarColorFor, initialsOf, formatPhone } from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    paper: { width: 520, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { gap: 14 },
    contactCard: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 12,
      padding: "10px 14px",
    },
    contactName: { fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary },
    contactNumber: { fontSize: 11.5, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    changeLink: { marginLeft: "auto" },
    queueDot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },
    channelIcon: { fontSize: 14, marginRight: 2 },
    online: { fontSize: 11, color: AEROS.success, marginLeft: 4 },
    offline: { fontSize: 11, color: AEROS.error, marginLeft: 4 },
    chips: { display: "flex", gap: 6, flexWrap: "wrap" },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 99,
      padding: "4px 12px 4px 4px",
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      background: "none",
      cursor: "pointer",
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      "&:hover": { borderColor: alpha(AEROS.cyan, 0.4) },
    },
    chipOn: {
      color: theme.palette.text.primary,
      backgroundColor: "rgba(160,68,255,0.15)",
      borderColor: "rgba(160,68,255,0.4)",
    },
    chipAvatar: {
      width: 20,
      height: 20,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 8,
      fontWeight: 800,
      color: "#07080D",
    },
    chipMe: { background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})` },
    placeholder: { color: theme.palette.aeros.textTertiary },
    cyan: { color: stateText(theme, AEROS.cyan, "info") },
    openPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      borderRadius: 99,
      padding: "3px 9px",
      whiteSpace: "nowrap",
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    greetingCode: { fontFamily: "ui-monospace, monospace", color: stateText(theme, AEROS.cyan, "info") },
    greetingSelect: { fontSize: 11, width: "100%", "& .MuiSelect-root": { padding: 0 } },
  };
});

const filter = createFilterOptions({ trim: true });

const IconChannel = (channel, className) => {
  switch (channel) {
    case "facebook":
      return <Facebook className={className} style={{ color: "#3b5998" }} />;
    case "instagram":
      return <Instagram className={className} style={{ color: "#e1306c" }} />;
    default:
      return <WhatsApp className={className} style={{ color: "#25d366" }} />;
  }
};

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;
  const isAdmin = user.profile === "admin";

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(user.id);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const [openTicket, setOpenTicket] = useState(null);
  const [quickMessages, setQuickMessages] = useState([]);
  const [greetingOn, setGreetingOn] = useState(false);
  const [greetingId, setGreetingId] = useState("");

  // Badge "Ticket #N aberto": consulta antes de tentar criar.
  useEffect(() => {
    if (!modalOpen || !selectedContact?.id) {
      setOpenTicket(null);
      return;
    }
    api
      .get(`/tickets/open-for-contact/${selectedContact.id}`)
      .then(({ data }) => setOpenTicket(data?.ticket || null))
      .catch(() => setOpenTicket(null));
  }, [modalOpen, selectedContact]);

  // Reabrir com o mesmo contato (mesma referência) precisa repor a seleção
  // que o fechar limpou — por isso modalOpen entra na dependência.
  useEffect(() => {
    if (modalOpen && initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact, modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    setSelectedUserId(user.id);
    api
      .get("/whatsapp", { params: { companyId, session: 0 } })
      .then(({ data }) => setWhatsapps(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
    if (whatsappId !== null && whatsappId !== undefined) setSelectedWhatsapp(whatsappId);
    if (user.queues?.length === 1) setSelectedQueue(user.queues[0].id);
    if (isAdmin) {
      api
        .get("/users/list", { params: { companyId } })
        .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
        .catch(() => setUsers([]));
    }
    api
      .get("/quick-messages/list", { params: { companyId, userId: user.id, isOficial: "false" } })
      .then(({ data }) => setQuickMessages(Array.isArray(data) ? data.filter((q) => q.message) : []))
      .catch(() => setQuickMessages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      api
        .get("contacts", { params: { searchParam } })
        .then(({ data }) => setOptions(data.contacts))
        .catch((err) => toastError(err))
        .finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const reset = () => {
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
    setGreetingOn(false);
    setGreetingId("");
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    if (selectedQueue === "") {
      toast.error(i18n.t("newTicketModal.selectQueue"));
      return;
    }
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        queueId: selectedQueue !== "" ? selectedQueue : null,
        whatsappId: selectedWhatsapp !== "" ? selectedWhatsapp : null,
        userId: selectedUserId || user.id,
        status: "open",
      });
      await sendGreeting(ticket);
      onClose(ticket);
      reset();
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        setOpenAlert(true);
        setUserTicketOpen(errorData.ticket?.user?.name || "Outro usuário");
        setQueueTicketOpen(errorData.ticket?.queue?.name || "Sem fila");
        return;
      }
      if (err.response?.status === 409) {
        const ticket = JSON.parse(err.response.data.error);
        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name);
          setQueueTicketOpen(ticket?.queue?.name);
        } else {
          onClose(ticket);
          reset();
        }
        return;
      }
      toastError(err);
    }
    setLoading(false);
  };

  // Saudação ao abrir: a resposta rápida escolhida vai como primeira mensagem
  // do ticket (mesmo endpoint do chat), com as variáveis resolvidas.
  const sendGreeting = async (ticket) => {
    const qm = quickMessages.find((q) => q.id === greetingId);
    if (!greetingOn || !qm || !ticket?.id) return;
    const contact = ticket.contact || selectedContact || {};
    const hour = new Date().getHours();
    const body = substituteVariables(qm.message, user.name, {
      firstName: String(contact.name || "").split(" ")[0],
      name: contact.name || "",
      ms: hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite",
      protocol: ticket.id ? String(ticket.id) : "",
      date: new Date().toLocaleDateString("pt-BR"),
      hour: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      ticket_id: String(ticket.id),
      queue: ticket.queue?.name || "",
      connection: ticket.whatsapp?.name || "",
    });
    try {
      await api.post(`/messages/${ticket.id}`, { body, fromMe: true, read: 1 });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({ name: `${params.inputValue}` });
    }
    return filtered;
  };

  const renderOption = (option) =>
    option.number ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
        {IconChannel(option.channel, classes.channelIcon)}
        {option.name} · {formatPhone(option.number)}
      </span>
    ) : (
      `${i18n.t("newTicketModal.add")} ${option.name}`
    );

  const queue = user.queues?.find((q) => q.id === selectedQueue);
  const assignees = users.filter((u) => u.id !== user.id);

  return (
    <>
      <Dialog open={modalOpen} onClose={handleClose} maxWidth={false} classes={{ paper: classes.paper }}>
        <div className={m.form}>
          <div className={m.header}>
            <div>
              <div className={m.title}>{i18n.t("newTicketModal.title")}</div>
              <div className={m.subtitle}>{i18n.t("newTicketModal.subtitle")}</div>
            </div>
            <IconButton className={m.closeButton} onClick={handleClose} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={`${m.body} ${classes.body}`}>
            {selectedContact ? (
              <div className={classes.contactCard}>
                <div
                  className={m.avatar}
                  style={{ width: 40, height: 40, fontSize: 13, backgroundColor: avatarColorFor(selectedContact.id) }}
                >
                  {selectedContact.urlPicture && !/nopicture\.png$/i.test(selectedContact.urlPicture) ? (
                    <img src={selectedContact.urlPicture} alt={selectedContact.name} />
                  ) : (
                    initialsOf(selectedContact.name)
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.contactName}>{selectedContact.name}</div>
                  <div className={classes.contactNumber}>
                    {selectedContact.isGroup ? selectedContact.number : formatPhone(selectedContact.number)}
                  </div>
                </div>
                {openTicket && (
                  <span className={classes.openPill} title={openTicket.user?.name ? `${i18n.t("newTicketModal.openBy")} ${openTicket.user.name}` : undefined}>
                    <HistoryIcon /> {i18n.t("newTicketModal.openTicket", { id: openTicket.id })}
                  </span>
                )}
                {initialContact?.id === undefined && (
                  <button type="button" className={`${m.linkButton} ${classes.changeLink}`} onClick={() => setSelectedContact(null)}>
                    {i18n.t("newTicketModal.change")}
                  </button>
                )}
              </div>
            ) : (
              <div>
                <label className={m.fieldLabel}>{i18n.t("newTicketModal.fieldLabel")}</label>
                <Autocomplete
                  options={options}
                  loading={loading}
                  clearOnBlur
                  autoHighlight
                  freeSolo
                  clearOnEscape
                  getOptionLabel={(o) => (o.number ? `${o.name} - ${o.number}` : `${o.name}`)}
                  renderOption={renderOption}
                  filterOptions={createAddContactOption}
                  onChange={handleSelectOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      className={m.input}
                      placeholder={i18n.t("newTicketModal.searchPlaceholder")}
                      autoFocus
                      onChange={(e) => setSearchParam(e.target.value)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            )}

            <div className={m.grid2}>
              <div>
                <label className={m.fieldLabel}>{i18n.t("newTicketModal.queue")}</label>
                <Select
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  className={m.input}
                  value={selectedQueue}
                  onChange={(e) => setSelectedQueue(e.target.value)}
                  MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                  renderValue={() =>
                    queue ? (
                      <>
                        <span className={classes.queueDot} style={{ backgroundColor: queue.color || AEROS.cyan }} />
                        {queue.name}
                      </>
                    ) : (
                      <span className={classes.placeholder}>{i18n.t("newTicketModal.selectQueue")}</span>
                    )
                  }
                >
                  {(user.queues || []).map((q) => (
                    <MenuItem dense key={q.id} value={q.id}>
                      <span className={classes.queueDot} style={{ backgroundColor: q.color || AEROS.cyan, marginRight: 8 }} />
                      {q.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div>
                <label className={m.fieldLabel}>{i18n.t("newTicketModal.connection")}</label>
                <Select
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  className={m.input}
                  value={selectedWhatsapp}
                  onChange={(e) => setSelectedWhatsapp(e.target.value)}
                  MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                  renderValue={() => {
                    const w = whatsapps.find((x) => x.id === selectedWhatsapp);
                    return w ? (
                      <>
                        {IconChannel(w.channel, classes.channelIcon)}
                        {w.name}
                      </>
                    ) : (
                      <span className={classes.placeholder}>{i18n.t("newTicketModal.selectConnection")}</span>
                    );
                  }}
                >
                  {whatsapps.map((w) => (
                    <MenuItem dense key={w.id} value={w.id}>
                      {IconChannel(w.channel, classes.channelIcon)}
                      <span style={{ marginLeft: 6 }}>{w.name}</span>
                      <span className={w.status === "CONNECTED" ? classes.online : classes.offline}>({w.status})</span>
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            <div className={m.optionBox}>
              <FlashOnIcon />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={m.optionTitle}>{i18n.t("newTicketModal.greetingTitle")}</div>
                {greetingOn ? (
                  <Select
                    value={greetingId}
                    onChange={(e) => setGreetingId(e.target.value)}
                    displayEmpty
                    disableUnderline
                    className={classes.greetingSelect}
                    renderValue={(v) => {
                      const qm = quickMessages.find((q) => q.id === v);
                      return qm ? (
                        <span className={m.optionDesc}>
                          {i18n.t("newTicketModal.greetingUses")} <span className={classes.greetingCode}>/{qm.shortcode}</span>
                        </span>
                      ) : (
                        <span className={m.optionDesc}>{i18n.t("newTicketModal.greetingPick")}</span>
                      );
                    }}
                    MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                  >
                    {quickMessages.map((q) => (
                      <MenuItem key={q.id} value={q.id} dense>
                        /{q.shortcode}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <div className={m.optionDesc}>{i18n.t("newTicketModal.greetingDesc")}</div>
                )}
              </div>
              <Switch
                className={aeros.toggleSwitchSmall}
                checked={greetingOn}
                onChange={(e) => setGreetingOn(e.target.checked)}
                color="primary"
                disabled={quickMessages.length === 0}
              />
            </div>

            {isAdmin && (
              <div>
                <label className={m.fieldLabel}>{i18n.t("newTicketModal.assignTo")}</label>
                <div className={classes.chips}>
                  <button
                    type="button"
                    className={`${classes.chip} ${selectedUserId === user.id ? classes.chipOn : ""}`}
                    onClick={() => setSelectedUserId(user.id)}
                    aria-pressed={selectedUserId === user.id}
                  >
                    <span className={`${classes.chipAvatar} ${classes.chipMe}`}>EU</span>
                    {i18n.t("newTicketModal.me")}
                  </button>
                  {assignees.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      className={`${classes.chip} ${selectedUserId === u.id ? classes.chipOn : ""}`}
                      onClick={() => setSelectedUserId(u.id)}
                      aria-pressed={selectedUserId === u.id}
                    >
                      <span className={classes.chipAvatar} style={{ backgroundColor: avatarColorFor(u.id) }}>
                        {initialsOf(u.name)}
                      </span>
                      {u.name.split(" ")[0]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={m.footer}>
            <div className={m.footerActions}>
              <Button variant="outlined" className={m.cancelButton} onClick={handleClose} disabled={loading}>
                {i18n.t("newTicketModal.buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={m.submitButton}
                startIcon={<ChatIcon />}
                disabled={!selectedContact || loading}
                onClick={() => handleSaveTicket(selectedContact.id)}
              >
                {i18n.t("newTicketModal.buttons.ok")}
                {loading && <CircularProgress size={22} className={m.buttonProgress} />}
              </Button>
            </div>
          </div>
        </div>

        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={() => setContactModalOpen(false)}
            onSave={(contact) => setSelectedContact(contact)}
          />
        )}
        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={() => {
              setOpenAlert(false);
              setUserTicketOpen("");
              setQueueTicketOpen("");
            }}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog>
    </>
  );
};

export default NewTicketModal;
